import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE } from './config';

function extractNameFromEmail(email) {
  if (!email) return null;
  const namePart = email.split('@')[0];
  // Replace dots/underscores with spaces, capitalize first letter
  const name = namePart.replace(/[._-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
  return name;
}

function ArboxSignup({ onSuccess, onError, email, userName, selectedAppId = null, jwt = null, googleToken = null }) {
  const [arboxEmail, setArboxEmail] = useState(email || '');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [error, setError] = useState(null);
  const [availableApps, setAvailableApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(selectedAppId || 'default');
  const [loadingApps, setLoadingApps] = useState(true);
  const [showAppSelector, setShowAppSelector] = useState(false);
  const dropdownRef = useRef(null);

  const name = userName || extractNameFromEmail(arboxEmail) || 'User';

  // Get current app's auth methods
  const currentApp = availableApps.find(app => app.app_id === selectedApp);
  const authMethods = currentApp?.auth_methods || ['credentials'];
  const supportsCredentials = authMethods.includes('credentials');
  const supportsSms = authMethods.includes('sms');

  // Reset OTP state when app changes
  useEffect(() => {
    setOtpSent(false);
    setOtpCode('');
    setPhone('');
    setError(null);
  }, [selectedApp]);

  // Fetch available applications for signup
  useEffect(() => {
    const fetchAvailableApps = async () => {
      try {
        setLoadingApps(true);
        let endpoint, headers = {};

        if (jwt) {
          // Existing user adding another app - exclude apps they already have
          endpoint = `${API_BASE}/available-applications-for-signup`;
          headers.Authorization = `Bearer ${jwt}`;
        } else {
          // New user - show all available applications
          endpoint = `${API_BASE}/available-applications`;
        }

        const response = await axios.get(endpoint, { headers });
        const apps = response.data.applications || [];
        setAvailableApps(apps);

        // If a specific app was requested and it exists, select it
        if (selectedAppId && apps.some(app => app.app_id === selectedAppId)) {
          setSelectedApp(selectedAppId);
        } else if (apps.length > 0) {
          // Default to first available app
          setSelectedApp(apps[0].app_id);
        }
      } catch (err) {
        console.error('Failed to fetch available applications:', err);
        // Fallback to default app
        setAvailableApps([{ app_id: 'default', name: 'Arbox', description: 'Default Arbox application', icon_path: '/icons/arbox_icon.webp' }]);
        setSelectedApp('default');
      } finally {
        setLoadingApps(false);
      }
    };

    fetchAvailableApps();
  }, [selectedAppId, jwt]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAppSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoadingOtp(true);
    setError(null);

    if (!phone) {
      setError('Please enter your phone number');
      setLoadingOtp(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/request-otp`, { 
        phone,
        app_id: selectedApp
      });
      if (response.data.success) {
        setOtpSent(true);
        setError(null);
      } else {
        setError(response.data.error || 'Failed to send OTP code');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to send OTP code. Please try again.';
      setError(errorMessage);
    }
    setLoadingOtp(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate that an app is selected and available
    if (!selectedApp || availableApps.length === 0) {
      setError('No application selected or available for signup.');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        app_id: selectedApp,
      };

      // Add authentication fields based on auth method
      if (supportsCredentials) {
        if (!arboxEmail || !password) {
          setError('Please enter email and password');
          setLoading(false);
          return;
        }
        payload.email = arboxEmail;
        payload.password = password;
      } else if (supportsSms) {
        if (!phone || !otpCode) {
          setError('Please enter phone number and OTP code');
          setLoading(false);
          return;
        }
        payload.phone = phone;
        payload.otp_code = otpCode;
      }

      // For new users, include the Google token for secure verification
      if (googleToken && !jwt) {
        payload.google_token = googleToken;
      }

      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('🚀 ArboxSignup sending payload (SECURE):', {
          ...payload,
          password: payload.password ? '***' : undefined,
          otp_code: payload.otp_code ? '***' : undefined
        });
        console.log('🔐 User email will be extracted from JWT token on backend');
        console.log('🎫 JWT token present:', !!jwt);
        console.log('📡 Authorization header will be included:', !!jwt);
      }

      let endpoint;
      const headers = {};

      if (jwt) {
        // Existing user adding another application
        endpoint = `${API_BASE}/add-application`;
        headers.Authorization = `Bearer ${jwt}`;
      } else if (googleToken) {
        // New user signing up
        endpoint = `${API_BASE}/signup`;
      } else {
        // This should not happen - either JWT or Google token should be present
        setError('Authentication error: Missing required tokens');
        setLoading(false);
        return;
      }

      const response = await axios.post(endpoint, payload, { headers });
      if (response.data.success) {
        onSuccess(response.data.token);
      } else {
        setError(response.data.error || 'Signup failed');
        if (onError) onError(response.data.error);
      }
    } catch (err) {
      let errorMessage = 'Signup failed';
      if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        const serverError = err.response.data?.error;
        
        if (status === 401) {
          errorMessage = serverError || 'Invalid credentials. Please check your information.';
        } else {
          errorMessage = serverError || `Server error (HTTP ${status}). Please try again.`;
        }
      } else if (err.request) {
        // Network error - no response received
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
      setError(errorMessage);
      if (onError) onError(errorMessage);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <form className="bg-white p-8 rounded-lg shadow-md w-full max-w-md" onSubmit={handleSubmit}>
        {/* Welcoming message */}
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-extrabold text-blue-700 mb-2">
            Hi{ name ? ` ${name}` : '' }, glad you're here!
          </h1>
          <p className="text-gray-600 text-lg">To help automate your gym experience, we need access to your account.</p>
        </div>
        {/* Attention-grabbing info box */}
        <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded shadow-sm text-yellow-900 font-medium text-center">
          {supportsSms 
            ? "We'll send you an OTP code via SMS to verify your phone number."
            : "Don't worry! We never store your credentials. We only use them once to generate a secure token."}
        </div>
        {/* Application Selector */}
        {loadingApps ? (
          <div className="w-full p-4 mb-4 border rounded bg-gray-50 text-gray-500 text-center">
            Loading applications...
          </div>
        ) : availableApps.length === 0 ? (
          <div className="w-full p-4 mb-4 border rounded bg-yellow-50 text-yellow-700 text-center">
            No new applications available. You may already have access to all available apps.
          </div>
        ) : (
          <>
            {/* App Selector Dropdown */}
            <div className="mb-4 relative" ref={dropdownRef}>
              <label className="block mb-2 text-sm font-medium text-gray-700">Application</label>
              {(() => {
                const currentApp = availableApps.find(app => app.app_id === selectedApp);
                return currentApp ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => availableApps.length > 1 && setShowAppSelector(!showAppSelector)}
                      className={`w-full flex items-center p-3 border rounded-lg transition-colors ${
                        availableApps.length > 1
                          ? 'hover:bg-gray-50 cursor-pointer border-gray-300'
                          : 'border-gray-200 cursor-default'
                      } bg-white`}
                    >
                      <img
                        src={currentApp.icon_path}
                        alt={currentApp.name}
                        className="w-12 h-12 rounded-lg mr-3 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-gray-900">{currentApp.name}</h3>
                        <p className="text-sm text-gray-600">{currentApp.description}</p>
                      </div>
                      {availableApps.length > 1 && (
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${showAppSelector ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>

                    {/* Dropdown Menu */}
                    {showAppSelector && availableApps.length > 1 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                        <div className="py-1">
                          {availableApps.map(app => (
                            <button
                              key={app.app_id}
                              type="button"
                              onClick={() => {
                                setSelectedApp(app.app_id);
                                setShowAppSelector(false);
                              }}
                              className={`w-full flex items-center px-3 py-2 text-left transition-colors ${
                                selectedApp === app.app_id
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'hover:bg-gray-50 text-gray-900'
                              }`}
                            >
                              <img
                                src={app.icon_path}
                                alt={app.name}
                                className="w-8 h-8 rounded-md mr-3 object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              <div className="flex-1">
                                <div className="font-medium">{app.name}</div>
                                <div className="text-sm text-gray-500">{app.description}</div>
                              </div>
                              {selectedApp === app.app_id && (
                                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
          </>
        )}

        {/* Credentials authentication form */}
        {supportsCredentials && (
          <>
            <label className="block mb-2">Email</label>
            <input
              type="email"
              className="w-full p-2 mb-4 border rounded"
              value={arboxEmail}
              onChange={e => setArboxEmail(e.target.value)}
              required
            />
            <label className="block mb-2">Password</label>
            <input
              type="password"
              className="w-full p-2 mb-4 border rounded"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </>
        )}

        {/* SMS authentication form */}
        {supportsSms && (
          <>
            <label className="block mb-2">Phone Number</label>
            <input
              type="tel"
              className="w-full p-2 mb-4 border rounded"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="0541234567"
              required
              disabled={otpSent}
            />
            {!otpSent ? (
              <button
                type="button"
                onClick={handleRequestOtp}
                className="w-full bg-green-600 text-white py-2 rounded disabled:opacity-50 mb-4"
                disabled={loadingOtp || !phone}
              >
                {loadingOtp ? 'Sending OTP...' : 'Send OTP Code'}
              </button>
            ) : (
              <>
                <label className="block mb-2">OTP Code</label>
                <input
                  type="text"
                  className="w-full p-2 mb-4 border rounded"
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                  maxLength={6}
                />
                <button
                  type="button"
                  onClick={() => {
                    setOtpSent(false);
                    setOtpCode('');
                    setError(null);
                  }}
                  className="w-full bg-gray-400 text-white py-2 rounded mb-4"
                >
                  Change Phone Number
                </button>
              </>
            )}
          </>
        )}

        {error && <div className="text-red-600 mb-2">{error}</div>}
        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 rounded disabled:opacity-50"
          disabled={loading || availableApps.length === 0 || (supportsSms && !otpSent)}
        >
          {loading ? 'Signing up...' : availableApps.length === 0 ? 'No Apps Available' : 'Sign Up'}
        </button>
      </form>
    </div>
  );
}

export default ArboxSignup; 